import { useUser } from './user';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { isUnchanged } from '@/store/cache-primitives';
import { useApi } from '@lib/api';
import { whereNotNull } from '@/lib/type-helpers';
import { signalrListen } from '@lib/signalr';
import type dtoHashes from '@/lib/dto-hashes.json';

type Dto = { id: number };
export type DtoName = keyof typeof dtoHashes;

type LoadFunction<T> = (brandId: number) => Promise<T[]>;
type CreateFunction<T, A> = (brandId: number, args: A) => Promise<T>;
type UpdateFunction<T, U> = (brandId: number, id: number, args: U) => Promise<T>;
type DeleteFunction = (brandId: number, id: number) => Promise<void>;
type UsageFunction<TUsage> = (brandId: number, id: number) => Promise<TUsage>;

type Methods<T extends Dto, A, U, TUsage> = {
  load: LoadFunction<T>;
  create?: CreateFunction<T, A>;
  update?: UpdateFunction<T, U>;
  delete?: DeleteFunction;
  usage?: UsageFunction<TUsage>;
  signalR: string;
  // Name of the DTO that `load` returns. Keys this store's persisted snapshot and
  // invalidates it when the DTO schema changes (see store/warm-boot.ts). Omit to opt
  // the store out of snapshot caching — it then always cold-loads.
  dto?: DtoName;
};

export function useGlobalStore<
  M extends Methods<T, A, U, TUsage>,
  T extends Dto = Awaited<ReturnType<M['load']>>[0],
  A = M extends { create: CreateFunction<T, infer A> } ? A : any,
  U = M extends { update: UpdateFunction<T, infer U> } ? U : any,
  TUsage = M extends { usage: UsageFunction<infer TUsage> } ? TUsage : any,
>(
  methods: M
): GlobalStoreBase<T> &
  (M extends { create: CreateFunction<T, A> } ? { create: (args: A) => Promise<T> } : {}) &
  (M extends { update: UpdateFunction<T, U> } ? { update: (id: number, args: U) => Promise<T> } : {}) &
  (M extends { delete: DeleteFunction } ? { delete: (id: number) => Promise<void> } : {}) &
  (M extends { usage: UsageFunction<TUsage> } ? { usage: (id: number) => Promise<TUsage> } : {});

export function useGlobalStore<T extends Dto, A, U, TUsage>(
  methods: Methods<T, A, U, TUsage>
): GlobalStoreBase<T> & {
  create?: (args: A) => Promise<T>;
  update?: (id: number, args: U) => Promise<T>;
  delete?: (id: number) => Promise<void>;
  usage?: (id: number) => Promise<TUsage>;
} {
  const api = useApi();

  const base = globalStoreBase(
    () => {
      const brandId = useUser().brandId;
      return methods.load.call(api, brandId);
    },
    methods.signalR,
    methods.dto
  );

  return {
    ...base,

    create:
      methods.create &&
      (async (args: A) => {
        const brandId = useUser().brandId;
        const item = await methods.create!.call(api, brandId, args);
        // Replace rather than push: admin pages keep a local copy and watch
        // `loaded` shallowly, and the SignalR reload that follows returns
        // identical data, so it would not replace the array either.
        if (base.loaded.value.every((x) => x.id !== item.id)) base.loaded.value = [...base.loaded.value, item];

        return item;
      }),

    update:
      methods.update &&
      (async (id: number, args: U) => {
        const brandId = useUser().brandId;
        const item = await methods.update!.call(api, brandId, id, args);
        const index = base.loaded.value.findIndex((i) => i.id === item.id);
        if (index > -1) {
          base.loaded.value[index] = item;
        } else {
          base.loaded.value.push(item);
        }

        return item;
      }),

    delete:
      methods.delete &&
      (async (id: number) => {
        const brandId = useUser().brandId;
        await methods.delete!.call(api, brandId, id);

        base.loaded.value = base.loaded.value.filter((i) => i.id !== id);
      }),

    usage:
      methods.usage &&
      (async (id: number) => {
        const brandId = useUser().brandId;
        return await methods.usage!.call(api, brandId, id);
      }),
  };
}

type GlobalStoreBase<T extends Dto> = ReturnType<typeof globalStoreBase<T>>;
function globalStoreBase<T extends Dto>(
  loadAllFunction: () => Promise<T[]>,
  signalRMethodName: string,
  dtoName?: DtoName
) {
  const loaded = ref([]) as Ref<T[]>;
  const isLoaded = ref(false);

  const byId: ComputedRef<Record<number, T>> = computed(() => {
    return Object.fromEntries(loaded.value.map((item) => [item.id, item]));
  });

  let onLoadedReplaced: ((items: T[]) => void) | undefined;

  let pendingLoad: Promise<T[]> | null = null;
  async function loadAll() {
    if (pendingLoad) return pendingLoad;

    pendingLoad = loadAllFunction();
    try {
      const fresh = await pendingLoad;
      // Keep the existing array when a revalidation returns identical data (the common
      // case after a warm-boot seed), so byId stays stable and watchers on individual
      // items don't refire. The persistence callback still runs either way — the
      // snapshot's savedAt must be refreshed or it would expire after MAX_AGE_MS even
      // for data that is merely unchanged (see lib/snapshot-cache.ts).
      if (!isUnchanged(loaded.value, fresh)) {
        loaded.value = fresh;
      }
      isLoaded.value = true;
      onLoadedReplaced?.(loaded.value);
      return loaded.value;
    } finally {
      pendingLoad = null;
    }
  }

  // Apply the list a write endpoint answered with — the same list loadAll fetches, already
  // reflecting the write. Without it the table keeps rendering the old values until the
  // write's SignalR event arrives and loadAll returns, which reads as a flicker: the value
  // reverts to what it was for as long as that round trip takes.
  function replaceAll(items: T[]) {
    if (!isUnchanged(loaded.value, items)) {
      loaded.value = items;
    }
    isLoaded.value = true;
    onLoadedReplaced?.(loaded.value);
  }

  // Seed from a persisted snapshot: counts as loaded, but does not re-persist.
  function seed(items: T[]) {
    loaded.value = items;
    isLoaded.value = true;
  }

  // Called with the fresh list after every successful loadAll — used by warm-boot
  // snapshot persistence (store/warm-boot.ts). In-place create/update/delete mutations
  // are covered too, because their SignalR events trigger loadAll shortly after.
  function onReplaced(callback: (items: T[]) => void) {
    onLoadedReplaced = callback;
  }

  function getById(id: number): T | undefined {
    return loaded.value.find((item) => item.id === id);
  }

  // Preserves order of passed in ids (NOT loaded's order)
  function getByIds(ids: number[]): T[] {
    return ids.map((id) => getById(id)).filter(whereNotNull);
  }

  function listen() {
    signalrListen(signalRMethodName, loadAll, { listenForever: true });
  }

  return { loaded, isLoaded, byId, loadAll, replaceAll, getById, getByIds, listen, seed, onReplaced, dtoName };
}
